import { OperatingPoint, RealtimeSession } from 'speechmatics';

import { TranscriptionController, TranscriptionMessage } from './types';

const KYRON_BACKEND_SPEECHMATICS_KEY_ENDPOINT = '/api/v1/realtime_transcription/speechmatics_key';
const transcriptionConfig = {
  language: 'en',
  max_delay: 3,
  operating_point: OperatingPoint.Enhanced,
  enable_partials: true,
};
const MICROPHONE_CHUNK_SIZE_MS = 250;

export class RealtimeTranscriptionController implements TranscriptionController {
  session: RealtimeSession | null = null;

  handlePartialTranscript: (message: TranscriptionMessage) => void;

  handleFinalTranscript: (message: TranscriptionMessage) => void;

  stream: MediaStream | null = null;

  recorder: MediaRecorder | null = null;

  paused: boolean = false; // If true, don't send audio to Speechmatics, but keep the session open.

  constructor(
    handlePartialTranscript: (message: TranscriptionMessage) => void,
    handleFinalTranscript: (message: TranscriptionMessage) => void,
  ) {
    this.handlePartialTranscript = handlePartialTranscript;
    this.handleFinalTranscript = handleFinalTranscript;
  }

  public async initiate(onRecognitionStart: () => void): Promise<void> {
    // Get a temporary token to use for client sided communication directly with Speechmatics
    const key: string = await this.fetchSpeechmaticsKey();
    if (key === null) {
      throw new Error('Speechmatics key not retrieved - disabling transcription');
    }
    this.session = new RealtimeSession(key);

    this.registerEventListeners(onRecognitionStart);
    await this.startRealtimeTranscriptionSession();
  }

  private async fetchSpeechmaticsKey(): Promise<string> {
    return fetch(KYRON_BACKEND_SPEECHMATICS_KEY_ENDPOINT)
      .then(response => response.json())
      .then(data => data.transcription_key);
  }

  private registerEventListeners(onRecognitionStart: () => void) {
    this.session!.addListener('RecognitionStarted', () => {
      console.debug('Speechmatics RecognitionStarted');
      onRecognitionStart();
    });
    this.session!.addListener('Error', error => {
      console.error('Speechmatics session error', error);
    });

    this.session!.addListener('AddPartialTranscript', this.handlePartialTranscript);
    this.session!.addListener('AddTranscript', this.handleFinalTranscript);
  }

  /*
    Throughout the open-ended question, we are recording audio (locally) in 1000ms chunks.
    If this.paused is set to false, then we discard the current audio chunk. If it is set to true, 
    then we send the current audio chunk to Speechmatics for transcription. 
    E.g. if the assistant is currently talking, then we don't want to send the audio to Speechmatics
    (because it will transcribe what the assistant said as part of the student's response, unless
    the student is wearing headphones).
    */
  private async startRealtimeTranscriptionSession() {
    await this.session!.start({ transcription_config: transcriptionConfig });
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    this.recorder = new MediaRecorder(this.stream, {
      mimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 16000,
    });

    this.recorder.start(MICROPHONE_CHUNK_SIZE_MS);
    this.recorder.ondataavailable = event => {
      if (event.data.size > 0) {
        if (!this.paused) {
          this.session!.sendAudio(event.data);
        }
      }
    };
  }

  public stop() {
    if (this.session) this.session.stop();
    if (this.recorder) this.recorder.stop();

    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        if (track.readyState === 'live') {
          track.stop();
        }
      });
    }
  }

  public pause(value: boolean = true) {
    this.paused = value;
  }

  public awaitTranscription(): Promise<string | void> {
    // Since transcriptions are delivered in near real-time, we shouldn't have to wait for them
    // TODO: we could potentially wait for a final transcript to be delivered here, but this would slow things down
    // (and the user usually waits for their message to stop updating before they submit it anyway)
    return Promise.resolve();
  }
}
